import { useCallback, useEffect, useState } from 'react';
import { filterColumn, targetId } from '@/components/chat/chatTarget';
import type { ChatTarget } from '@/components/chat/types';
import { supabase } from '@/lib/supabase';
import { isMuteActive, muteExpiry, type MuteDuration } from '@/utils/chatMute';
import { logger } from '@/utils/logger';

type MuteRow = {
  id: string;
  event_id: string | null;
  direct_chat_id: string | null;
  department_chat_id: string | null;
  muted_until: string | null;
};

/** chatKey()-style identity for a mute row, so lookups are one map hit. */
function rowKey(row: MuteRow): string | null {
  if (row.event_id) return `event:${row.event_id}`;
  if (row.direct_chat_id) return `direct:${row.direct_chat_id}`;
  if (row.department_chat_id) return `department:${row.department_chat_id}`;
  return null;
}

/**
 * This viewer's muted threads.
 *
 * One query for every mute they hold -- the table has at most a handful of rows per
 * person, so paging or per-thread lookups would cost more than they save. RLS restricts
 * it to their own rows, so no filter is needed here.
 */
export function useChatMute(userId: string | null) {
  const [mutedUntilByKey, setMutedUntilByKey] = useState<Map<string, string | null>>(new Map());
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!userId) {
      setMutedUntilByKey(new Map());
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from('chat_mutes')
      .select('id, event_id, direct_chat_id, department_chat_id, muted_until');
    if (error) {
      logger.error('useChatMute load failed', error);
      setLoading(false);
      return;
    }
    const next = new Map<string, string | null>();
    for (const row of (data ?? []) as MuteRow[]) {
      const key = rowKey(row);
      if (key) next.set(key, row.muted_until);
    }
    setMutedUntilByKey(next);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const isMuted = useCallback(
    (key: string): boolean =>
      mutedUntilByKey.has(key) && isMuteActive(mutedUntilByKey.get(key)),
    [mutedUntilByKey]
  );

  const mutedUntil = useCallback(
    (key: string): string | null | undefined =>
      isMuted(key) ? mutedUntilByKey.get(key) : undefined,
    [isMuted, mutedUntilByKey]
  );

  /**
   * Replace whatever mute this thread has with a new one.
   *
   * Delete then insert rather than upsert: the uniqueness that would drive an upsert is
   * three partial indexes (one per key column), which PostgREST cannot infer from an
   * onConflict list. Two round trips, no ambiguity.
   */
  const mute = useCallback(
    async (target: ChatTarget, duration: MuteDuration) => {
      if (!userId) return;
      const column = filterColumn(target);
      const id = targetId(target);
      const key = `${target.kind}:${id}`;
      const until = muteExpiry(duration);

      // Optimistic: the menu closes on click and should not flicker back to "not muted".
      setMutedUntilByKey((prev) => new Map(prev).set(key, until));

      await supabase.from('chat_mutes').delete().eq('user_id', userId).eq(column, id);
      const { error } = await supabase
        .from('chat_mutes')
        .insert({ user_id: userId, [column]: id, muted_until: until });
      if (error) {
        logger.error('useChatMute mute failed', error);
        void refetch();
      }
    },
    [userId, refetch]
  );

  const unmute = useCallback(
    async (target: ChatTarget) => {
      if (!userId) return;
      const column = filterColumn(target);
      const id = targetId(target);
      const key = `${target.kind}:${id}`;

      setMutedUntilByKey((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });

      const { error } = await supabase
        .from('chat_mutes')
        .delete()
        .eq('user_id', userId)
        .eq(column, id);
      if (error) {
        logger.error('useChatMute unmute failed', error);
        void refetch();
      }
    },
    [userId, refetch]
  );

  return { isMuted, mutedUntil, mute, unmute, loading, refetch };
}
